import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useWorkflowStore } from '@/lib/workflows/store';
import { validateWorkflow } from '@/lib/workflows/validator';
import { executeWorkflow } from '@/lib/workflows/executor';
import Button from '../buttons/Button';
import { useUser } from '@/utils/useUser';
import { CollaboratorsModal } from './CollaboratorsModal';
import { Awareness } from 'y-protocols/awareness';
import { useYjsAwareness } from '@/lib/workflows/useYjsAwareness';

import useLocale from 'next-translate/useTranslation';

import { SimpleTooltip } from '@/components/ui/tooltip';

interface WorkflowToolbarProps {
  onAddNode: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  awareness?: Awareness | null;
  onUseTemplate?: () => void;
  readOnly?: boolean;
  interactionMode?: 'pan' | 'select';
  onToggleMode?: () => void;
}

export function WorkflowToolbar({
  onAddNode,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  awareness,
  onUseTemplate,
  readOnly = false,
  interactionMode = 'pan',
  onToggleMode,
}: WorkflowToolbarProps) {
  const { t } = useLocale('common');
  const router = useRouter();
  const [isSaving, setIsSaving] = useState(false);
  const activeCollaborators = useYjsAwareness(awareness);
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);

  // Store
  const nodes = useWorkflowStore((state) => state.nodes);
  const edges = useWorkflowStore((state) => state.edges);
  const workflowId = useWorkflowStore((state) => state.workflowId);
  const workflowName = useWorkflowStore((state) => state.workflowName);
  const workflowOwnerId = useWorkflowStore((state) => state.workflowOwnerId);
  const collaboratorCount = useWorkflowStore(
    (state) => state.collaborators.length,
  );
  const collaborators = useWorkflowStore((state) => state.collaborators);
  const hasUnsavedChanges = useWorkflowStore(
    (state) => state.hasUnsavedChanges,
  );
  const isExecuting = useWorkflowStore((state) => state.isExecuting);
  const markSaved = useWorkflowStore((state) => state.markSaved);
  const startExecution = useWorkflowStore((state) => state.startExecution);
  const stopExecution = useWorkflowStore((state) => state.stopExecution);
  const setWorkflowName = useWorkflowStore((state) => state.setWorkflowName);
  const isTeamMember = useWorkflowStore((state) => state.isTeamMember);
  const { user } = useUser();
  const isOwner = workflowOwnerId ? workflowOwnerId === user?.id : false;
  const isEditorCollaborator =
    !isOwner && user
      ? collaborators.some((c) => c.user_id === user.id && c.role === 'editor')
      : false;
  const canEdit = isOwner || isEditorCollaborator || isTeamMember;

  // Autosave on changes (owner and editors)
  useEffect(() => {
    if (!workflowId || !hasUnsavedChanges || !canEdit) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      // Fire and forget; button save still available
      handleSave();
    }, 4000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [workflowId, hasUnsavedChanges, canEdit, nodes, edges]);

  // Forward the share-link token (?share=...) on writes so users whose edit
  // permission comes from an editor share link pass authorization in
  // /api/workflows/[id] (PUT).
  const shareQueryParam =
    typeof router.query.share === 'string' && router.query.share
      ? `?share=${encodeURIComponent(router.query.share)}`
      : '';

  // Save workflow
  const handleSave = async () => {
    if (!workflowId) return;

    try {
      setIsSaving(true);

      const response = await fetch(
        `/api/workflows/${workflowId}${shareQueryParam}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            flow_data: { nodes, edges },
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to save workflow');
      }

      markSaved();
    } catch (error) {
      console.error('Failed to save workflow:', error);
      alert('Failed to save workflow. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Run workflow
  const handleRun = () => {
    // Check if workflow has upload nodes that might need user attention
    const hasUploadNodes = nodes.some((node) => node.type === 'upload');

    if (hasUploadNodes) {
      const confirmed = confirm(
        'Before running the workflow, please make sure you have:\n\n' +
          '• Uploaded your own images/videos in the upload nodes\n' +
          '• Updated any text inputs with your content\n' +
          '• Reviewed the settings in each node\n\n' +
          'Continue with workflow execution?',
      );
      if (!confirmed) return;
    }

    // Validate workflow
    const validation = validateWorkflow(nodes, edges);

    if (!validation.isValid) {
      alert(
        `Cannot run workflow:\n${validation.errors.map((e) => `• ${e.message}`).join('\n')}`,
      );
      return;
    }

    // Show warnings if any
    if (validation.warnings.length > 0) {
      const proceed = confirm(
        `Warnings:\n${validation.warnings.map((w) => `• ${w.message}`).join('\n')}\n\nContinue anyway?`,
      );
      if (!proceed) return;
    }

    // Show execution modal
    setShowExecutionModal(true);
  };

  // Execute workflow
  const handleExecute = async () => {
    setShowExecutionModal(false);

    try {
      const executionId = `exec-${Date.now()}`;
      startExecution(executionId);

      await executeWorkflow(nodes, edges);

      stopExecution();
      alert('Workflow executed successfully!');
    } catch (error: any) {
      stopExecution();
      alert(`Workflow execution failed: ${error.message}`);
    }
  };

  // Edit workflow name
  const handleStartEditName = () => {
    setEditedName(workflowName);
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    const trimmedName = editedName.trim();

    if (!trimmedName || trimmedName === workflowName) {
      setIsEditingName(false);
      return;
    }

    // Update local state immediately
    setWorkflowName(trimmedName);
    setIsEditingName(false);

    // Update on backend if workflow exists
    if (workflowId) {
      try {
        const response = await fetch(
          `/api/workflows/${workflowId}${shareQueryParam}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: trimmedName }),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to update workflow name');
        }
      } catch (error) {
        console.error('Failed to update workflow name:', error);
        alert('Failed to update workflow name. Please try again.');
      }
    }
  };

  const handleExport = () => {
    const exportData = {
      name: workflowName,
      description: '',
      category: 'custom',
      flow_data: { nodes, edges },
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${workflowName.replace(/[^a-zA-Z0-9-_ ]/g, '')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <>
      <div className='bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between'>
        {/* Left side */}
        <div className='flex items-center gap-2 sm:gap-4 min-w-0'>
          <button
            onClick={() => router.push('/workflows')}
            className='flex-shrink-0 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors'
          >
            <svg
              className='w-5 h-5'
              fill='none'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path d='M15 19l-7-7 7-7' />
            </svg>
          </button>

          <div className='min-w-0'>
            {readOnly ? (
              <h1 className='font-semibold text-gray-900 dark:text-white text-base sm:text-lg truncate max-w-[120px] sm:max-w-none'>
                {workflowName}
              </h1>
            ) : isEditingName ? (
              <input
                type='text'
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onBlur={handleSaveName}
                onKeyDown={handleNameKeyDown}
                autoFocus
                className='font-semibold text-gray-900 dark:text-white text-base sm:text-lg border border-primary-500 dark:border-primary-600 rounded px-2 py-1 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 w-full max-w-[150px] sm:max-w-none'
                maxLength={100}
              />
            ) : (
              <h1
                className='font-semibold text-gray-900 dark:text-white text-base sm:text-lg cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors truncate max-w-[120px] sm:max-w-none'
                onClick={handleStartEditName}
                title='Click to edit workflow name'
              >
                {workflowName}
              </h1>
            )}
            {!readOnly && (
              <p className='text-xs text-gray-500 dark:text-gray-400 hidden sm:block'>
                {isSaving
                  ? t('Saving...')
                  : hasUnsavedChanges
                    ? t('Unsaved changes')
                    : t('All changes saved')}
              </p>
            )}
          </div>

          {/* Undo/Redo Buttons - hidden on mobile */}
          {!readOnly && (
            <div className='hidden sm:flex items-center gap-1 ml-4 border-l border-gray-200 dark:border-gray-700 pl-4'>
              <button
                onClick={onUndo}
                disabled={!canUndo}
                className='p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
                title='Undo (Cmd/Ctrl+Z)'
              >
                <svg
                  className='w-4 h-4 text-gray-700 dark:text-gray-300'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6' />
                </svg>
              </button>
              <button
                onClick={onRedo}
                disabled={!canRedo}
                className='p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
                title='Redo (Cmd/Ctrl+Shift+Z or Cmd/Ctrl+Y)'
              >
                <svg
                  className='w-4 h-4 text-gray-700 dark:text-gray-300'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M21 10H11a8 8 0 00-8 8v2m18-10l-6 6m6-6l-6-6' />
                </svg>
              </button>
            </div>
          )}

          {/* Pan/Select Mode Toggle */}
          {!readOnly && onToggleMode && (
            <div className='hidden sm:flex items-center gap-0.5 ml-2 border-l border-gray-200 dark:border-gray-700 pl-3'>
              <button
                onClick={interactionMode === 'pan' ? undefined : onToggleMode}
                className={`p-2 rounded-md transition-colors ${
                  interactionMode === 'pan'
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}
                title='Pan mode - drag to pan the canvas'
              >
                {/* Hand icon */}
                <svg
                  className='w-4 h-4'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11' />
                </svg>
              </button>
              <button
                onClick={
                  interactionMode === 'select' ? undefined : onToggleMode
                }
                className={`p-2 rounded-md transition-colors ${
                  interactionMode === 'select'
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}
                title='Select mode - drag to select nodes'
              >
                {/* Cursor/pointer icon */}
                <svg
                  className='w-4 h-4'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122' />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Right side */}
        <div className='flex items-center gap-1.5 sm:gap-3'>
          {/* Add Node Button */}
          {!readOnly && (
            <Button
              variant='outline'
              onClick={onAddNode}
              className='flex items-center gap-2 !px-2 sm:!px-4'
            >
              <svg
                className='w-4 h-4'
                fill='none'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path d='M12 4v16m8-8H4' />
              </svg>
              <span className='hidden sm:inline'>{t('Add Node')}</span>
            </Button>
          )}

          {/* Collaborators Button - hidden on mobile */}
          {workflowId && (
            <SimpleTooltip title={t('Collaborate')}>
              <Button
                variant='outline'
                onClick={() => setShowCollaboratorsModal(true)}
                className='hidden md:flex items-center gap-1 !px-2.5'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M17 21v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2' />
                  <circle cx='9' cy='7' r='4' />
                  <path d='M23 21v-2a4 4 0 00-3-3.87' />
                  <path d='M16 3.13a4 4 0 010 7.75' />
                </svg>
                {activeCollaborators.length > 0 ? (
                  <span className='text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full flex items-center gap-1'>
                    <span className='w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse'></span>
                    {activeCollaborators.length}
                  </span>
                ) : collaboratorCount > 0 ? (
                  <span className='text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full'>
                    {collaboratorCount}
                  </span>
                ) : null}
              </Button>
            </SimpleTooltip>
          )}

          {/* Save Button - hidden on mobile (autosave) */}
          {!readOnly && (
            <SimpleTooltip
              title={hasUnsavedChanges ? t('Save') : t('All changes saved')}
            >
              <Button
                variant='outline'
                onClick={handleSave}
                isLoading={isSaving}
                disabled={!hasUnsavedChanges || !workflowId}
                className='hidden sm:flex !px-2.5'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4' />
                </svg>
              </Button>
            </SimpleTooltip>
          )}

          {/* Export Button - hidden on mobile */}
          {workflowId && (
            <SimpleTooltip title='Export'>
              <Button
                variant='outline'
                onClick={handleExport}
                className='hidden sm:flex !px-2.5'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' />
                </svg>
              </Button>
            </SimpleTooltip>
          )}

          {/* Use Template Button (for template previews) */}
          {onUseTemplate && (
            <Button
              variant='primary'
              onClick={onUseTemplate}
              className='flex items-center gap-2 !px-2 sm:!px-4'
            >
              <svg
                className='w-4 h-4'
                fill='none'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path d='M12 4v16m8-8H4' />
              </svg>
              <span className='hidden sm:inline'>Use Template</span>
            </Button>
          )}
        </div>
      </div>

      {/* Execution Confirmation Modal */}
      {showExecutionModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50'>
          <div className='bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4'>
            <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
              Run Workflow?
            </h3>

            <div className='space-y-3 mb-6'>
              <div className='flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700'>
                <span className='text-sm text-gray-600 dark:text-gray-400'>
                  Total Nodes:
                </span>
                <span className='text-sm font-medium text-gray-900 dark:text-white'>
                  {nodes.length}
                </span>
              </div>

              <p className='text-xs text-gray-500 dark:text-gray-400 mt-4'>
                The workflow will stop if any node fails or if you have
                insufficient credits.
              </p>
            </div>

            <div className='flex gap-3'>
              <Button
                variant='outline'
                onClick={() => setShowExecutionModal(false)}
                className='flex-1'
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={handleExecute}
                className='flex-1'
              >
                Run Workflow
              </Button>
            </div>
          </div>
        </div>
      )}

      {workflowId && (
        <CollaboratorsModal
          open={showCollaboratorsModal}
          onClose={() => setShowCollaboratorsModal(false)}
          workflowId={workflowId}
          isOwner={isOwner}
          awareness={awareness}
        />
      )}
    </>
  );
}
